import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/app-header";
import { Check, Loader2 } from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { trackEvent } from "@/lib/analytics";

export default function CheckoutSuccess() {
  const [, setLocation] = useLocation();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [tier, setTier] = useState<string>("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      setStatus("error");
      return;
    }

    fetch(`/api/stripe/checkout/success?session_id=${sessionId}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setTier(data.tier);
          setStatus("success");
          trackEvent("checkout_completed", {
            plan: ["monthly", "yearly", "lifetime"].includes(data.tier)
              ? data.tier
              : "unknown",
          });
          queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        } else {
          setStatus("error");
        }
      })
      .catch(() => setStatus("error"));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-md mx-auto px-4 py-20">
        <Card className="p-8 text-center">
          {status === "loading" && (
            <div className="space-y-4">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
              <h2 className="text-xl font-semibold" data-testid="text-checkout-loading">
                Confirming your purchase...
              </h2>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center">
                <Check className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold" data-testid="text-checkout-success">
                Welcome to DanceMirror Pro!
              </h2>
              <p className="text-muted-foreground" data-testid="text-checkout-tier">
                {tier === "lifetime"
                  ? "Your lifetime access is now active. You have access to all premium features forever."
                  : `Your ${tier} subscription is now active. You have access to all premium features.`}
              </p>
              <div className="flex flex-col gap-3 mt-6">
                <Button onClick={() => setLocation("/")} data-testid="button-start-practicing">
                  Start Practicing
                </Button>
                <Button variant="outline" onClick={() => setLocation("/library")} data-testid="button-go-library">
                  Go to Library
                </Button>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold" data-testid="text-checkout-error">
                Something went wrong
              </h2>
              <p className="text-muted-foreground">
                We couldn't verify your payment. If you were charged, your subscription will be activated shortly.
              </p>
              <Button variant="outline" onClick={() => setLocation("/pricing")} data-testid="button-back-pricing">
                Back to Pricing
              </Button>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
